import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { BadgeCheck, Loader2, MailCheck, Send, ShieldAlert, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Campo } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { api, ErroApi, type SessaoDemo } from '@/lib/api';
import { useAcesso } from '@/auth/EstadoAcesso';

/**
 * Demonstração do produto.
 *
 * Dois estados na mesma página:
 *  - pedido do acesso (`/demo`), que envia a mensagem de validação;
 *  - validação (`/demo?token=...`), que libera **um** acesso por e-mail.
 *
 * O material gerado na demonstração sai com a marca "cópia não autorizada".
 */
export function Demo() {
  const [parametros, setParametros] = useSearchParams();
  const token = parametros.get('token');
  const { demo, guardarDemo, limparDemo } = useAcesso();

  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [validando, setValidando] = useState(Boolean(token));
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [pedidoFeito, setPedidoFeito] = useState(false);
  const [sessao, setSessao] = useState<SessaoDemo | null>(null);

  /* Validação do link recebido por e-mail */
  useEffect(() => {
    if (!token) return;
    let ativo = true;
    setValidando(true);
    api
      .confirmarDemo(token)
      .then((resposta) => {
        if (!ativo) return;
        guardarDemo(resposta);
        setSessao(resposta);
        setAviso(resposta.aviso);
        // Limpa o token da barra de endereços depois de usado.
        parametros.delete('token');
        setParametros(parametros, { replace: true });
      })
      .catch((falha) => {
        if (ativo) setErro(falha instanceof Error ? falha.message : 'Não foi possível validar o link.');
      })
      .finally(() => {
        if (ativo) setValidando(false);
      });
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const pedir = async () => {
    setEnviando(true);
    setErro(null);
    setAviso(null);
    try {
      const resposta = await api.solicitarDemo({ nome: nome.trim() || undefined, email: email.trim() });
      setPedidoFeito(true);
      setAviso(resposta.aviso ? `${resposta.mensagem} ${resposta.aviso}` : resposta.mensagem);
    } catch (falha) {
      const mensagem =
        falha instanceof ErroApi
          ? falha.message
          : 'Não foi possível pedir a demonstração agora. Tente novamente em instantes.';
      setErro(mensagem);
    } finally {
      setEnviando(false);
    }
  };

  const reenviar = async () => {
    setEnviando(true);
    setErro(null);
    try {
      const resposta = await api.reenviarDemo(email.trim());
      setAviso(resposta.mensagem);
    } catch {
      setErro('Não foi possível reenviar agora.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6 py-4">
      <header className="space-y-2 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">Demonstração do gerador</h1>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Monte um miolo completo e baixe o PDF para conferir na impressão. Para liberar, confirmamos o seu e-mail —
          assim cada pessoa tem direito a <strong className="text-foreground">um acesso</strong>.
        </p>
      </header>

      {validando ? (
        <p className="flex items-center justify-center gap-2 rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Validando seu acesso...
        </p>
      ) : null}

      {sessao ? (
        <section className="space-y-3 rounded-xl border border-primary/40 bg-primary/5 p-5">
          <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <BadgeCheck className="size-4 text-primary" />
            Acesso de demonstração liberado
          </p>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Enviamos a confirmação para <strong className="text-foreground">{sessao.email}</strong>. Este e-mail já
            usou o acesso de demonstração, válido até{' '}
            {new Date(sessao.expiraEm).toLocaleDateString('pt-BR')}.
          </p>
          <ul className="space-y-1.5 text-xs leading-relaxed text-muted-foreground">
            <li>· Todas as páginas saem com a marca “cópia não autorizada”.</li>
            <li>· O material é de uso pessoal: não pode ser revendido.</li>
            <li>· Para remover a marca e liberar todos os miolos, escolha um plano.</li>
          </ul>
          <div className="flex flex-wrap gap-2 pt-1">
            <Button asChild>
              <Link to="/criar">Montar meu miolo agora</Link>
            </Button>
            <Button asChild variant="contorno">
              <Link to="/planos">Ver planos e liberar o acesso</Link>
            </Button>
            <Button variant="fantasma" tamanho="pequeno" onClick={limparDemo}>
              Encerrar demonstração
            </Button>
          </div>
        </section>
      ) : null}

      {demo && !sessao ? (
        <p className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
          Você já tem uma demonstração liberada neste navegador para <strong className="text-foreground">{demo.email}</strong>.
          <Button asChild variant="fantasma" tamanho="pequeno" className="ml-2">
            <Link to="/criar">Continuar montando</Link>
          </Button>
        </p>
      ) : null}

      {erro ? (
        <p className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          <TriangleAlert className="mt-0.5 size-4 shrink-0" />
          {erro}
        </p>
      ) : null}

      {aviso && !sessao ? (
        <p className="flex items-start gap-2 rounded-lg border border-primary/40 bg-primary/5 p-4 text-sm text-foreground">
          <MailCheck className="mt-0.5 size-4 shrink-0 text-primary" />
          {aviso}
        </p>
      ) : null}

      {/* Pedido do acesso */}
      {!sessao && !validando ? (
        <section className="space-y-4 rounded-xl border border-border bg-card p-5">
          <h2 className="text-sm font-semibold">Pedi minha demonstração</h2>

          <div className="grid gap-4 sm:grid-cols-2">
            <Campo rotulo="Seu nome (opcional)">
              <Input
                value={nome}
                onChange={(evento) => setNome(evento.target.value)}
                placeholder="Como você se chama"
                maxLength={90}
                autoComplete="name"
              />
            </Campo>
            <Campo rotulo="Seu e-mail" dica="É para onde enviamos o link de validação.">
              <Input
                type="email"
                value={email}
                onChange={(evento) => setEmail(evento.target.value)}
                placeholder="voce@exemplo.com"
                maxLength={160}
                autoComplete="email"
              />
            </Campo>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={pedir} disabled={enviando || !email.includes('@')}>
              {enviando ? <Loader2 className="animate-spin" /> : <Send />}
              Enviar link de validação
            </Button>
            {pedidoFeito ? (
              <Button variant="contorno" onClick={reenviar} disabled={enviando}>
                Reenviar o link
              </Button>
            ) : null}
            <Button asChild variant="fantasma">
              <Link to="/planos">Prefiro ver os planos</Link>
            </Button>
          </div>
        </section>
      ) : null}

      <section className="space-y-3 rounded-xl border border-border bg-muted/40 p-5">
        <p className="flex items-center gap-2 text-sm font-semibold">
          <ShieldAlert className="size-4 text-primary" />
          Como protegemos você e o nosso trabalho
        </p>
        <ul className="space-y-1.5 text-xs leading-relaxed text-muted-foreground">
          <li>· O acesso só é liberado depois da validação do e-mail — é o que evita cadastros automáticos.</li>
          <li>· Cada e-mail recebe um único acesso de demonstração, com validade limitada.</li>
          <li>· Todo PDF de demonstração sai marcado como cópia não autorizada, em todas as páginas.</li>
          <li>· Guardamos apenas o seu e-mail e a data do pedido, para enviar o link e nada além disso.</li>
        </ul>
      </section>
    </div>
  );
}
